import { ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

/**
 * A drop-down as OMGLoft's (lib/v2/V2Menu.svelte): a button like its filter pills (an icon, the chosen item, an
 * arrow) and a list under it, or over it by the bottom edge (`up`). Keys: arrows up and down, Enter, Escape; a
 * click beside it closes it.
 */
export function Menu<T extends string>({
  options,
  value,
  icon,
  label,
  onChange,
  up = false,
}: {
  options: readonly { value: T; label: string }[];
  value: T;
  icon: ReactNode;
  /** The name for a screen reader and the tooltip: the button shows the icon and the chosen item. */
  label: string;
  onChange(value: T): void;
  up?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const current = options.find((option) => option.value === value);

  useEffect(() => {
    if (!open) return;
    (list.current?.querySelector<HTMLButtonElement>('[aria-selected="true"]') ?? list.current?.querySelector('button'))?.focus();
    const close = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    addEventListener('pointerdown', close);
    return () => removeEventListener('pointerdown', close);
  }, [open]);

  const keys = (event: KeyboardEvent) => {
    if (!open) return;
    if (event.key === 'Escape') {
      event.stopPropagation();
      setOpen(false);
      root.current?.querySelector<HTMLButtonElement>('.dropdown__trigger')?.focus();
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const items = [...(list.current?.querySelectorAll<HTMLButtonElement>('button') ?? [])];
    const index = items.indexOf(document.activeElement as HTMLButtonElement);
    items[(index + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length]?.focus();
  };

  return (
    <div className="dropdown" ref={root} onKeyDown={keys}>
      <button type="button" className="dropdown__trigger" aria-haspopup="listbox" aria-expanded={open} aria-label={label} title={label} onClick={() => setOpen(!open)}>
        {icon}
        <span className="dropdown__current">{current?.label ?? '…'}</span>
        <ChevronDown className={`dropdown__chevron${up ? ' dropdown__chevron--up' : ''}`} aria-hidden="true" />
      </button>
      {open && (
        <ul className={`dropdown__list${up ? ' dropdown__list--up' : ''}`} role="listbox" aria-label={label} ref={list}>
          {options.map((option) => (
            <li key={option.value}>
              <button
                type="button"
                role="option"
                aria-selected={option.value === value}
                onClick={() => {
                  setOpen(false);
                  if (option.value !== value) onChange(option.value);
                }}
              >
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
